import { MenuItem, Button } from "@blueprintjs/core";
import React from "react";
import { Field } from "typegraph";
import ArticlePlaylist from "./entities/ArticlePlaylist";
import CountResult from "common/entities/CountResult";
import { ID } from "common/entities/IEntity";
import { FlexJustify } from "common/fragments/Flex";
import Loading from "common/fragments/Loading";
import NonIdealState from "common/fragments/NonIdealState";
import { BlockView } from "common/views/block/BlockView.view";
import { ButtonVariant, ButtonView } from "common/views/button/Button.view";
import { CardView } from "common/views/card/Card.view";
import { DropdownSelectView } from "common/views/dropdownSelect/DropdownSelect.view";
import { OptionDropdownSelect } from "common/views/dropdownSelect/OptionDropdownSelect";
import HeadingView from "common/views/heading/Heading.view";
import { ImageView } from "common/views/image/Image.view";
import PageLinkView from "common/views/pageLink/PageLink.view";
import PagingView from "common/views/paging/Paging.view";
import { RowsView } from "common/views/rows/Rows.view";
import Query from "core/api/QueryComponent";
import Label from "core/components/label/Label";
import { IPanelProps, Panel } from "core/decorators/Panel";
import { lastItem } from "core/util/ArrayUtil";
import { IBreadcrumb } from "core/util/IBreadcrumb";
import { clamp } from "core/util/MathUtils";
import StringUtil from "core/util/StringUtil";
import UrlUtil from "core/util/UrlUtil";
import ArticleListPanelConfig from "./config/ArticleListPanelConfig";
import constants from "./constants";
import { IconNames } from "@blueprintjs/icons";
import ServiceRegistry from "core/util/ServiceRegistry";
import Playlist from "./entities/Playlist";
import AppToaster from "core/util/AppToaster";

class Article {
	@Field()
	public id: ID;

	@Field()
	public displayName: string;

	@Field()
	public name: string;

	@Field()
	public keywords: string;

	@Field()
	public iconUrl: string;

	@Field({ entity: Playlist })
	public playlist: Playlist;
}

function mapQueryParams(args: any) {
	const params = {};

	Object.entries(args).forEach(([key, value]) => {
		if (value) {
			params[key] = value;
		}
	});

	return { params };
}

class ArticleQuery {
	@Field({
		entity: Article,
		aliasFor: "article_many",
		params: mapQueryParams
	})
	public entities: null | Article[];

	@Field({
		entity: CountResult,
		aliasFor: "article_count",
		params: mapQueryParams
	})
	public count: CountResult;
}

@Panel({
	id: "kb-article-list",
	configClass: ArticleListPanelConfig,
	pageParams: true
})
class ArticleListPanel extends React.Component<
	IPanelProps<ArticleListPanelConfig>
> {
	public render() {
		const { config, pageParams } = this.props;

		if (!config.knowledgeBaseGuid) {
			return null;
		}

		const params = {
			knowledgeBaseGuid: config.knowledgeBaseGuid,
			pageSize: StringUtil.toInteger(
				pageParams[constants.PARAM_PAGE_SIZE],
				config.defaultPageSize || 12
			),
			pageNumber: StringUtil.toInteger(
				pageParams[constants.PARAM_PAGE_NUMBER],
				0
			),
			queryFullText: !!config.queryFullText, // temp
			sort: "-a.publishedOn",
			trackRequest: !!config.saveSearchHistory // save search history
		};

		if (pageParams[constants.PARAM_QUERY]) {
			params["query"] = pageParams[constants.PARAM_QUERY];
		}

		let sortStr: string = pageParams[constants.PARAM_SORT];
		let sortDesc = false;
		if (sortStr && sortStr.startsWith("-")) {
			sortStr = sortStr.slice(1);
			sortDesc = true;
		}

		if (
			sortStr &&
			["a.publishedOn", "a.displayTitle"].indexOf(sortStr) > -1
		) {
			params["sort"] = sortStr;
			if (sortDesc) {
				params["sort"] = "-" + params["sort"];
			}
		}

		if (pageParams[constants.PARAM_CATEGORY_IDS]) {
			params["categoryIds"] = StringUtil.toIntegerList(
				pageParams[constants.PARAM_CATEGORY_IDS]
			);
		} else if (pageParams[constants.PARAM_CATEGORY_ID]) {
			params["categoryId"] = pageParams[constants.PARAM_CATEGORY_ID];
		}

		if (pageParams[constants.PARAM_CONTRIBUTOR_IDS]) {
			params["contributorIds"] = StringUtil.toIntegerList(
				pageParams[constants.PARAM_CONTRIBUTOR_IDS]
			);
		}

		if (pageParams[constants.PARAM_TAG_IDS]) {
			params["tagIds"] = StringUtil.toIntegerList(
				pageParams[constants.PARAM_TAG_IDS]
			);
		}

		if (pageParams[constants.PARAM_TAG_CONDITION] === "OR") {
			params["tagOrCondition"] = true;
		}

		return (
			<Query.Root entity={ArticleQuery} args={params}>
				{args => {
					let content: React.ReactNode;
					let total = 0;
					let start = 0;
					let end = 0;

					if (args.loading) {
						content = <Loading />;
					} else if (
						args.data &&
						args.data.entities &&
						args.data.entities.length > 0
					) {
						total = args.data.count.count;
						start = 1 + params.pageSize * params.pageNumber;
						end = clamp(start, total, start + params.pageSize - 1);
						content = (
							<RowsView viewKey="articles.rows">
								{args.data.entities.map(article =>
									this.renderArticle(
										article,
										args.forceRefresh
									)
								)}
							</RowsView>
						);
					} else {
						content = (
							<NonIdealState
								title={
									<Label
										k="articles.noresults.title"
										v="No Articles Found"
									/>
								}
								description={
									<Label
										k="articles.noresults.description"
										v="Try different search parameters."
									/>
								}
								action={
									<ButtonView
										viewKey="clear"
										onClick={this.resetFilters}
										variant={ButtonVariant.PRIMARY}
									>
										<Label
											k="search.reset"
											v="Reset Filters"
										/>
									</ButtonView>
								}
							/>
						);
					}

					return (
						<div>
							<BlockView viewKey="top">
								<FlexJustify>
									<BlockView viewKey="paging.info">
										<Label
											k="paging.results.count"
											v="${start} - ${end} of ${total} Results"
											args={{ start, end, total }}
										/>
									</BlockView>
									<div>
										{!!params["query"] ? (
											<Label
												k="sort.relevance"
												v="Sorting by search relevance"
											/>
										) : (
											<OptionDropdownSelect
												viewKey="sort"
												options={[
													{
														value: "-a.publishedOn",
														label: (
															<Label
																k="sort.publishedOn.desc"
																v="Newest"
															/>
														)
													},
													{
														value: "a.publishedOn",
														label: (
															<Label
																k="sort.publishedOn"
																v="Oldest"
															/>
														)
													},
													{
														value: "a.displayTitle",
														label: (
															<Label
																k="sort.title"
																v="Title A-Z"
															/>
														)
													},
													{
														value:
															"-a.displayTitle",
														label: (
															<Label
																k="sort.title.desc"
																v="Title Z-A"
															/>
														)
													}
												]}
												value={params["sort"]}
												onChange={this.setSort}
											>
												<Label k="sort" v="Sort By" />
											</OptionDropdownSelect>
										)}
									</div>
								</FlexJustify>
							</BlockView>
							<div>{content}</div>
							<BlockView viewKey="bottom">
								<FlexJustify>
									<div>
										<PagingView
											viewKey="paging"
											pageSize={params["pageSize"]}
											pageNumber={params["pageNumber"]}
											total={total}
											setPageNumber={this.setPageNumber}
										/>
									</div>
									<DropdownSelectView
										viewKey="pageSize"
										onChange={this.setPageSize}
										items={[12, 24]}
										itemRenderer={(
											size: number,
											{ handleSelect }
										) => {
											return (
												<MenuItem
													key={size}
													text={
														<Label
															k="pageSize"
															v="Show ${size}"
															args={{ size }}
														/>
													}
													onClick={handleSelect}
												/>
											);
										}}
									>
										<Label
											k="pageSize"
											v="Show ${size}"
											args={{ size: params["pageSize"] }}
										/>
									</DropdownSelectView>
								</FlexJustify>
							</BlockView>
						</div>
					);
				}}
			</Query.Root>
		);
	}

	private renderArticle = (article: Article, refresh: () => void) => {
		return (
			<React.Fragment>
				<PageLinkView
					key={article.id}
					viewKey="article.link"
					preserveParams={true}
					params={{
						[constants.PARAM_ARTICLE_ID]: article.id
					}}
					breadcrumbs={(target, breadcrumbs) => {
						let nextCrumbs: IBreadcrumb[] = [];

						// push current page's crumb
						const lastCrumb = lastItem(breadcrumbs);

						const currentPath = UrlUtil.getCurrentPath();
						// if last crumb was for the current page, use that one
						if (
							lastCrumb &&
							lastCrumb.target.pathname === currentPath
						) {
							nextCrumbs = [
								...breadcrumbs.slice(0, breadcrumbs.length - 1),
								{
									...lastCrumb,
									target: {
										pathname: currentPath,
										search: UrlUtil.getSearch()
									}
								}
							];
						} else {
							// else create a new one for the current page
							nextCrumbs = [
								...breadcrumbs,
								{
									key: "article-list",
									label: {
										k: "panel.article-list",
										v: "Article Search"
									},
									target: {
										pathname: currentPath,
										search: UrlUtil.getSearch()
									}
								}
							];
						}

						// push article crumb
						nextCrumbs.push({
							key: "article:" + article.id,
							label: article.displayName || article.name,
							target
						});

						return nextCrumbs;
					}}
				>
					<CardView viewKey="card" interactive={true}>
						<ImageView viewKey="card.icon" src={article.iconUrl} />
					</CardView>
				</PageLinkView>

				<BlockView
					viewKey="card.body"
					style={{ background: "#9e9e9e3d" }}
				>
					<HeadingView viewKey="card.title">
						{article.displayName || article.name}
						<Button
							style={{ float: "right" }}
							small={true}
							icon={
								!article.playlist
									? IconNames.STAR_EMPTY
									: IconNames.BOOKMARK
							}
							onClick={() =>
								this.toogleBookmark(article.id, refresh)
							}
						/>
					</HeadingView>
				</BlockView>
			</React.Fragment>
		);
	};

	private toogleBookmark(id, refresh) {
		const articleId = {
			articleId: Number(id)
		} as any;
		ServiceRegistry.getMutationService()
			.create(articleId, ArticlePlaylist)
			.then(res => {
				refresh();
			})
			.catch(err => {
				AppToaster.show({
					message: err.message,
					intent: "danger"
				});
			});
	}

	// always reseting page number to zero when something changes that would filter the results.
	private setPageNumber = (pageNumber: number) =>
		this.props.actions.pushPageParam(
			constants.PARAM_PAGE_NUMBER,
			pageNumber.toString()
		);
	private setPageSize = (pageSize: number) =>
		this.props.actions.pushPageParams(
			{ key: constants.PARAM_PAGE_NUMBER, value: 0 },
			{ key: constants.PARAM_PAGE_SIZE, value: pageSize }
		);
	private resetFilters = () =>
		this.props.actions.removePageParams(Object.values(constants));
	private setSort = (sort: string) =>
		this.props.actions.pushPageParam("sort", sort);
}



// WEBPACK FOOTER //
// ./src/panels/kb/ArticleList.panel.tsx